import { useMemo, useState } from 'react';
import CustomToolTip from '../CustomToolTip'
import Select from '../select/Select'
import { OrbitalInsightSatelliteType, OrbitStartOptionsType } from './orbital-insights-type';
import { ColumnDevider, Table, TableBody, TableBodyCell, TableBodyRow, TableContainer, TableHeader, TableHeaderCell } from './table/Table'
import { getAllOrbitNames, getOrbitalInsightDetails } from './helperFunction';
import { get_formated_time } from '../../../utils/commonFunctions';
import { DurationType } from './OrbitalInsights';
import DurationToggleButton from './DurationToggleButton';
import EmptySection from './EmptySection';
import { GeoFeatureSvg, GroundStationSvg } from '../svgs/Svgs';

interface PassDetailsPanelProps {
    data: OrbitalInsightSatelliteType | null;
    componentType: 'contacts' | 'features';
    durationType: DurationType;
    setDurationType: (durationType: DurationType) => void;
    getDuration: (duration: number) => string;
    orbitStartOption?: OrbitStartOptionsType
}

const PassDetailsPanel = ({ data, componentType, durationType, setDurationType, getDuration, orbitStartOption = 'Start Time' }: PassDetailsPanelProps) => {
    const [selectedOrbit, setSelectedOrbit] = useState<{
        'contacts': string;
        'features': string;
    }>({ 'contacts': 'All Orbits', 'features': 'All Orbits' })

    const OrbitsNames = useMemo(() => getAllOrbitNames(data, orbitStartOption), [data, orbitStartOption])
    const selectedOrbitName = useMemo(() => selectedOrbit[componentType], [selectedOrbit, componentType])

    const OrbitDetails = useMemo(() => selectedOrbitName === 'All Orbits' ? data?.allOrbits : getOrbitalInsightDetails(data, orbitStartOption).find(orbit => orbit.orbitNumber === Number(selectedOrbitName?.replace(/\D/g, ""))), [selectedOrbitName, data, componentType, orbitStartOption])
    const Passes = useMemo(() => componentType === 'contacts' ? OrbitDetails?.groundStationPasses || [] : OrbitDetails?.geoFeaturePasses || [], [OrbitDetails, componentType])

    const allPasses = useMemo(() => componentType === 'contacts' ? data?.allOrbits?.groundStationPasses || [] : data?.allOrbits?.geoFeaturePasses || [], [data?.allOrbits, componentType])

    const label = useMemo(() => componentType === 'contacts' ? 'Ground contacts' : 'Geofeatures', [componentType])
    const tableName = useMemo(() => componentType === 'contacts' ? 'Ground Station' : 'GeoFeature', [componentType])

    const emptyLabel = useMemo(() => componentType === 'contacts' ? 'Ground Contacts' : 'GeoFeatures', [componentType])
    const emptyIcon = useMemo(() => componentType === 'contacts' ? <GroundStationSvg /> : <GeoFeatureSvg />, [componentType])
    const title = useMemo(() => `No ${emptyLabel}`, [emptyLabel])
    const description = useMemo(() => `Select ${componentType === 'contacts' ? 'Ground Stations' : 'Task Plan'}, to generate ${componentType === 'contacts' ? 'Ground Pass' : 'GeoFeature'} Insights`, [componentType])

    if (allPasses.length === 0) {
        return (
            <div className='insights__modal__body'>
                <EmptySection icon={emptyIcon} title={title} description={description} />
            </div>
        )
    }

    return (
        <div className='insights__modal__body'>
            <div className='insights__modal__body__header'>
                <span className='insights__modal__body__header__text'>{label}</span>
                <Select style={{ width: '100px' }} placement="bottom" value={selectedOrbitName}
                    onSelect={(value) => { setSelectedOrbit((prev) => ({ ...prev, [componentType]: value })) }}
                >
                    <Select.Trigger className='insights__modal__select__trigger'>
                        <Select.Value placeholder="Select" />
                    </Select.Trigger>
                    <Select.Content className='select__container'>
                        <Select.Group>
                            {OrbitsNames.map((orbit: string) => (
                                <Select.Item key={orbit} value={orbit}>{orbit}</Select.Item>
                            ))}
                        </Select.Group>
                    </Select.Content>
                </Select>
            </div>
            <TableContainer className='h-90'>
                <Table key={componentType}>
                    <TableHeader>
                        <TableHeaderCell cellType='extra__medium'>{tableName}</TableHeaderCell>
                        <TableHeaderCell cellType='extra__medium'>Start time</TableHeaderCell>
                        <TableHeaderCell cellType='extra__medium'>End time </TableHeaderCell>
                        <TableHeaderCell cellType='medium'>Duration</TableHeaderCell>
                        <DurationToggleButton durationType={durationType} setDurationType={setDurationType} />
                    </TableHeader>
                    <TableBody>
                        <ColumnDevider className='devider__start' />
                        <ColumnDevider className='devider__center' />
                        <ColumnDevider className='devider__end' />
                        {Passes.map((pass, index) => {
                            const name = componentType === 'contacts' ? pass.groundStationName : pass.geoFeatureName
                            const startTime = get_formated_time(pass.startTime)
                            const endTime = get_formated_time(pass.endTime)
                            const duration = pass.duration
                            const uniqueId = `${componentType}-${index}`
                            return (
                                <TableBodyRow key={uniqueId}>
                                    <TableBodyCell cellType='extra__medium'>
                                        <div className='w-100 text-truncate'>
                                            <CustomToolTip title={name}>{name}</CustomToolTip>
                                        </div>
                                    </TableBodyCell>
                                    <TableBodyCell cellType='extra__medium'>
                                        <CustomToolTip title={startTime}>{startTime}</CustomToolTip>
                                    </TableBodyCell>
                                    <TableBodyCell cellType='extra__medium'>
                                        <CustomToolTip title={endTime}>{endTime}</CustomToolTip>
                                    </TableBodyCell>
                                    <TableBodyCell cellType='medium'>
                                        <CustomToolTip title={getDuration(duration)}>{getDuration(duration)}</CustomToolTip>
                                    </TableBodyCell>
                                </TableBodyRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}

export default PassDetailsPanel